import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import { SearchLg, Settings01, Mail01 } from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { quoteService, generateRandomQuoteRequest } from '@/data/mock-quotes';
import type { QuoteRequest, QuoteStatus, ProcessingStep } from '@/types/quote';
import { useTranslation } from 'react-i18next';
import { ProcessingSteps } from '@/components/application/quotes/processing-steps';

export const FreightForwarderQuotesPage = () => {
    const { t } = useTranslation(['pages:quotes', 'common']);
    const navigate = useNavigate();
    const [quotes, setQuotes] = useState<QuoteRequest[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<QuoteStatus | ''>('');
    const [lastUpdateTime, setLastUpdateTime] = useState<Date>(new Date());
    const [isProcessingActive, setIsProcessingActive] = useState(false);
    const processingQuotesRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

    const statusOptions = [
        { id: '', label: t('list.filters.allStatuses') },
        { id: 'pending', label: t('status.pending') },
        { id: 'auto_responded', label: t('status.autoResponded') },
        { id: 'requires_review', label: t('status.requiresReview') },
        { id: 'responded', label: t('status.responded') },
        { id: 'archived', label: t('status.archived') },
    ];

    const getShipperStatusColor = (status: QuoteRequest['shipperStatus']) => {
        switch (status) {
            case 'accepted':
                return 'success';
            case 'rejected':
                return 'error';
            case 'modification_requested':
                return 'warning';
            default:
                return 'gray';
        }
    };

    const translateShipperStatus = (status: QuoteRequest['shipperStatus']) => {
        if (!status) return '';
        const statusMap: Record<NonNullable<QuoteRequest['shipperStatus']>, string> = {
            accepted: 'shipperStatus.accepted',
            rejected: 'shipperStatus.rejected',
            modification_requested: 'shipperStatus.modificationRequested',
        };
        return t(statusMap[status]);
    };

    const getStatusColor = (status: QuoteStatus) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'auto_responded': return 'success';
            case 'requires_review': return 'warning';
            case 'responded': return 'success';
            case 'archived': return 'gray';
            default: return 'gray';
        }
    };

    const translateStatus = (status: QuoteStatus) => {
        const statusMap: Record<QuoteStatus, string> = {
            'pending': 'status.pending',
            'auto_responded': 'status.autoResponded',
            'requires_review': 'status.requiresReview',
            'responded': 'status.responded',
            'archived': 'status.archived',
            'completed': 'status.completed',
            'cancelled': 'status.cancelled',
        };
        return t(statusMap[status]);
    };

    const loadQuotes = async () => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 500));

        const allQuotes = quoteService.getAll();
        console.log(`[FF Quotes Page] Loading quotes, found ${allQuotes.length} total quotes`);
        console.log(`[FF Quotes Page] Quotes:`, allQuotes);
        setQuotes(allQuotes);

        setIsLoading(false);
    };

    useEffect(() => {
        loadQuotes();
        
        // Ricarica le quote periodicamente per vedere aggiornamenti di stato shipper
        // Non sovrascriviamo le quote in processing, ma aggiorniamo solo quelle completate
        const intervalId = setInterval(() => {
            const allQuotes = quoteService.getAll();
            setQuotes(prevQuotes => {
                // Mantieni le quote in processing locali se sono più recenti
                const updatedQuotes = allQuotes.map(serviceQuote => {
                    const localQuote = prevQuotes.find(q => q.id === serviceQuote.id);
                    // Se la quote locale è in processing e più recente, mantienila
                    if (localQuote?.isProcessing && 
                        localQuote.lastUpdated && 
                        serviceQuote.lastUpdated &&
                        localQuote.lastUpdated > serviceQuote.lastUpdated) {
                        return localQuote;
                    }
                    // Altrimenti usa la quote dal servizio
                    return serviceQuote;
                });
                
                // Aggiungi le nuove quote che non erano presenti prima
                const newQuotes = allQuotes.filter(serviceQuote => 
                    !prevQuotes.find(q => q.id === serviceQuote.id)
                );
                
                return updatedQuotes.concat(newQuotes);
            });
        }, 2000); // Ogni 2 secondi

        // Listener per eventi di creazione nuove quote
        const handleNewQuote = () => {
            console.log(`[FF Quotes Page] Received newQuoteRequestCreated event, reloading quotes...`);
            loadQuotes();
        };
        window.addEventListener('newQuoteRequestCreated', handleNewQuote);
        console.log(`[FF Quotes Page] Added event listener for newQuoteRequestCreated`);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener('newQuoteRequestCreated', handleNewQuote);
        };
    }, []);

    // Funzione per simulare il processing di una quote
    const simulateProcessing = (quoteId: string) => {
        const stepDurations = [800, 800, 1000, 1000, 800, 800]; // Durata di ogni step in ms

        const updateQuote = (stepIndex: number, progress: number) => {
            setQuotes(prevQuotes => {
                return prevQuotes.map(quote => {
                    if (quote.id !== quoteId) return quote;

                    const updatedSteps: ProcessingStep[] = quote.processingSteps?.map((step, idx) => {
                        if (idx < stepIndex) {
                            return { ...step, status: 'completed' };
                        } else if (idx === stepIndex) {
                            return { ...step, status: 'processing' };
                        } else {
                            return { ...step, status: 'pending' };
                        }
                    }) || [];

                    const updatedQuote = {
                        ...quote,
                        processingSteps: updatedSteps,
                        processingProgress: progress,
                        lastUpdated: new Date(),
                    };

                    // Salva l'aggiornamento nel servizio
                    quoteService.update(quoteId, {
                        processingSteps: updatedSteps,
                        processingProgress: progress,
                        lastUpdated: updatedQuote.lastUpdated,
                        isProcessing: true,
                    });

                    return updatedQuote;
                });
            });
            setLastUpdateTime(new Date());
        };

        const processStep = (stepIndex: number) => {
            if (stepIndex >= stepDurations.length) {
                // Processing completato
                const finalProgress = 100;

                setQuotes(prevQuotes => {
                    const updatedQuotes = prevQuotes.map(quote => {
                        if (quote.id !== quoteId) return quote;

                        const allStepsCompleted: ProcessingStep[] = quote.processingSteps?.map(step => ({
                            ...step,
                            status: 'completed' as const,
                        })) || [];

                        // Decidi random se auto-responded o requires_review (70% auto, 30% review)
                        const isAutoResponded = Math.random() < 0.7;
                        const randomProduct = quote.extractedData?.product || 'prodotto';
                        const randomQuantity = quote.extractedData?.quantity || 500;
                        const randomUnit = quote.extractedData?.unit || 'kg';
                        const randomOrigin = quote.extractedData?.origin || 'Origine';
                        const randomDestination = quote.extractedData?.destination || 'Destinazione';

                        let completedQuote;
                        if (isAutoResponded) {
                            completedQuote = {
                                ...quote,
                                isProcessing: false,
                                processingSteps: allStepsCompleted,
                                processingProgress: finalProgress,
                                status: 'auto_responded' as QuoteStatus,
                                autoResponded: true,
                                requiresManualReview: false,
                                reviewReason: undefined,
                                respondedAt: new Date(),
                                responseEmailId: `resp-${quoteId}`,
                                responseSubject: `Preventivo trasporto ${randomProduct} - ${quoteId.toUpperCase()}`,
                                responseBody: `Gentile ${quote.fromName || 'Cliente'},

grazie per la sua richiesta di preventivo per il trasporto di ${randomProduct}.

Siamo lieti di fornirle il seguente preventivo:

**Dettagli Trasporto:**
- Origine: ${randomOrigin}
- Destinazione: ${randomDestination}
- Quantità: ${randomQuantity} ${randomUnit}

**Preventivo:**
- Costo base: ${Math.floor(Math.random() * 5000 + 2000).toLocaleString('it-IT')} EUR
- Componenti aggiuntive: ${Math.floor(Math.random() * 1000 + 200).toLocaleString('it-IT')} EUR
- **Totale: ${Math.floor(Math.random() * 6000 + 3000).toLocaleString('it-IT')} EUR**

Il preventivo è valido per 30 giorni.

Restiamo a disposizione per qualsiasi chiarimento.

Cordiali saluti,
Il Team Logistico`,
                                extractedData: quote.extractedData || {
                                    product: randomProduct,
                                    quantity: randomQuantity,
                                    unit: randomUnit,
                                    origin: randomOrigin,
                                    destination: randomDestination,
                                },
                                lastUpdated: new Date(),
                            };
                        } else {
                            const reviewReasons = [
                                'Destinazione richiede ricarico speciale',
                                'Prodotto richiede certificazioni aggiuntive',
                                'Data di ritiro richiesta in weekend',
                                'Quantità supera i limiti standard',
                                'Richiesta urgente richiede approvazione manuale',
                            ];
                            completedQuote = {
                                ...quote,
                                isProcessing: false,
                                processingSteps: allStepsCompleted,
                                processingProgress: finalProgress,
                                status: 'requires_review' as QuoteStatus,
                                autoResponded: false,
                                requiresManualReview: true,
                                reviewReason: reviewReasons[Math.floor(Math.random() * reviewReasons.length)],
                                extractedData: quote.extractedData || {
                                    product: randomProduct,
                                    quantity: randomQuantity,
                                    unit: randomUnit,
                                    origin: randomOrigin,
                                    destination: randomDestination,
                                },
                                lastUpdated: new Date(),
                            };
                        }

                        // Salva la quote completata nel servizio
                        quoteService.update(quoteId, {
                            isProcessing: completedQuote.isProcessing,
                            processingSteps: completedQuote.processingSteps,
                            processingProgress: completedQuote.processingProgress,
                            status: completedQuote.status,
                            autoResponded: completedQuote.autoResponded,
                            requiresManualReview: completedQuote.requiresManualReview,
                            reviewReason: completedQuote.reviewReason,
                            respondedAt: completedQuote.respondedAt,
                            responseEmailId: completedQuote.responseEmailId,
                            responseSubject: completedQuote.responseSubject,
                            responseBody: completedQuote.responseBody,
                            extractedData: completedQuote.extractedData,
                            lastUpdated: completedQuote.lastUpdated,
                        });

                        return completedQuote;
                    });
                    const hasOtherProcessing = updatedQuotes.some(q => q.id !== quoteId && q.isProcessing);
                    setIsProcessingActive(hasOtherProcessing);

                    return updatedQuotes;
                });
                setLastUpdateTime(new Date());
                return;
            }

            const stepDuration = stepDurations[stepIndex];
            const stepProgress = (stepIndex / stepDurations.length) * 100;
            const progressIncrement = (100 / stepDurations.length) / (stepDuration / 100);

            updateQuote(stepIndex, stepProgress);

            let progress = stepProgress;
            const progressInterval = setInterval(() => {
                progress += progressIncrement;
                if (progress >= stepProgress + (100 / stepDurations.length)) {
                    clearInterval(progressInterval);
                    updateQuote(stepIndex, stepProgress + (100 / stepDurations.length));
                    setTimeout(() => processStep(stepIndex + 1), 100);
                } else {
                    updateQuote(stepIndex, progress);
                }
            }, 100);

            setTimeout(() => {
                clearInterval(progressInterval);
                updateQuote(stepIndex, stepProgress + (100 / stepDurations.length));
                processStep(stepIndex + 1);
            }, stepDuration);
        };

        processStep(0);
    };

    // Effect per generare nuove quote ogni 15-60 secondi
    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        let isMounted = true;

        const generateNextQuote = () => {
            if (!isMounted) return;

            const randomDelay = Math.floor(Math.random() * 10000 + 5000); // 5-15 secondi

            timeoutId = setTimeout(() => {
                if (!isMounted) return;

                const newQuote = generateRandomQuoteRequest();

                // Salva la nuova quote nel servizio usando il metodo add
                quoteService.add(newQuote);

                setQuotes(prevQuotes => {
                    return [newQuote, ...prevQuotes];
                });
                setIsProcessingActive(true);
                setLastUpdateTime(new Date());

                simulateProcessing(newQuote.id);

                generateNextQuote();
            }, randomDelay);
        };

        const initialDelay = Math.floor(Math.random() * 10000 + 5000); // 5-15 secondi per coerenza
        timeoutId = setTimeout(() => {
            generateNextQuote();
        }, initialDelay);

        return () => {
            isMounted = false;
            if (timeoutId) clearTimeout(timeoutId);
            processingQuotesRef.current.forEach(timeout => clearTimeout(timeout));
            processingQuotesRef.current.clear();
        };
    }, []);

    // Filter quotes
    const filteredQuotes = useMemo(() => {
        let filtered = [...quotes];

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(quote =>
                quote.subject.toLowerCase().includes(term) ||
                quote.body.toLowerCase().includes(term) ||
                quote.from.toLowerCase().includes(term) ||
                quote.fromName?.toLowerCase().includes(term)
            );
        }

        if (statusFilter) {
            filtered = filtered.filter(quote => quote.status === statusFilter);
        }

        filtered.sort((a, b) => b.receivedAt.getTime() - a.receivedAt.getTime());

        return filtered;
    }, [quotes, searchTerm, statusFilter]);

    const handleQuoteClick = (quote: QuoteRequest) => {
        navigate(`/freight-forwarder/quotes/${quote.id}`);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('list.loading')}</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-0 -mx-4 -my-6 sm:-mx-6 lg:-mx-8">
            {/* Quotes List */}
            <div className="flex flex-col w-full">
                <div className="px-4 py-6 sm:px-6 lg:px-8 space-y-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div>
                                <div className="flex items-center gap-2">
                                    <h1 className="text-2xl font-semibold text-primary">{t('title')}</h1>
                                    {isProcessingActive && (
                                        <div className="relative">
                                            <div className="h-2 w-2 rounded-full bg-brand-600 animate-pulse" />
                                            <div className="absolute inset-0 h-2 w-2 rounded-full bg-brand-600 animate-ping opacity-75" />
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                    <p className="text-tertiary">{t('subtitle')}</p>
                                    <span className="text-tertiary">•</span>
                                    <p className="text-xs text-tertiary">
                                        {t('list.lastUpdate')}: {lastUpdateTime.toLocaleTimeString('it-IT', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit'
                                        })}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <Button
                            onClick={() => navigate('/freight-forwarder/quotes/settings')}
                            color="secondary"
                            size="md"
                            iconLeading={Settings01}
                        >
                            {t('settingsButton')}
                        </Button>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-col gap-4 sm:flex-row">
                        <div className="flex-1">
                            <Input
                                placeholder={t('list.searchPlaceholder')}
                                value={searchTerm}
                                onChange={setSearchTerm}
                                icon={SearchLg}
                                size="md"
                            />
                        </div>
                        <div className="w-full sm:w-64">
                            <Select
                                items={statusOptions}
                                selectedKey={statusFilter}
                                onSelectionChange={(key) => setStatusFilter(key as QuoteStatus | '')}
                                size="md"
                            >
                                {(item) => (
                                    <Select.Item key={item.id} id={item.id}>
                                        {item.label}
                                    </Select.Item>
                                )}
                            </Select>
                        </div>
                    </div>

                    {/* Quotes List */}
                    <div className="rounded-xl bg-primary shadow-xs ring-1 ring-secondary">
                        {filteredQuotes.length === 0 ? (
                            <div className="p-8 text-center">
                                <Mail01 className="mx-auto h-12 w-12 text-tertiary mb-4" />
                                <p className="text-tertiary">{t('list.noQuotes')}</p>
                            </div>
                        ) : (
                            <ul className="divide-y divide-secondary">
                                {filteredQuotes.map((quote) => {
                                    const isNewQuote = quote.isProcessing && quote.receivedAt.getTime() > Date.now() - 2000;
                                    return (
                                        <li
                                            key={quote.id}
                                            className={`p-4 hover:bg-primary_hover cursor-pointer transition-all duration-300 ${isNewQuote ? 'ring-2 ring-brand-500 shadow-lg animate-in slide-in-from-top-2' : ''
                                                }`}
                                            onClick={() => handleQuoteClick(quote)}
                                        >
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                        <h3 className="font-semibold text-primary truncate text-md">
                                                            {quote.subject}
                                                        </h3>
                                                        <Badge color={getStatusColor(quote.status)} size="sm">
                                                            {translateStatus(quote.status)}
                                                        </Badge>
                                                    </div>
                                                    <div className="flex items-center gap-4 text-sm text-tertiary mb-2">
                                                                <span>{quote.fromName || quote.from}</span>
                                                                <span>•</span>
                                                                <span>{quote.receivedAt.toLocaleDateString('it-IT', {
                                                                    day: '2-digit',
                                                                    month: 'short',
                                                                    year: 'numeric',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                })}</span>
                                                            </div>
                                                            <p className="text-sm text-tertiary line-clamp-2">
                                                                {quote.body.substring(0, 150)}...
                                                            </p>
                                                            {quote.extractedData && (
                                                                <div className="mt-2 flex flex-wrap gap-2">
                                                                    {quote.extractedData.product && (
                                                                        <span className="inline-flex items-center rounded-md bg-secondary px-2 py-1 text-xs text-tertiary">
                                                                            {t('extracted.product')}: {quote.extractedData.product}
                                                                        </span>
                                                                    )}
                                                                    {quote.extractedData.destination && (
                                                                        <span className="inline-flex items-center rounded-md bg-secondary px-2 py-1 text-xs text-tertiary">
                                                                            {t('extracted.destination')}: {quote.extractedData.destination}
                                                                        </span>
                                                                    )}
                                                                    {quote.extractedData.quantity && (
                                                                        <span className="inline-flex items-center rounded-md bg-secondary px-2 py-1 text-xs text-tertiary">
                                                                            {t('extracted.quantity')}: {quote.extractedData.quantity} {quote.extractedData.unit}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                                                {quote.shipperStatus && (
                                                                    <Badge color={getShipperStatusColor(quote.shipperStatus)} size="sm">
                                                                        {translateShipperStatus(quote.shipperStatus)}
                                                                    </Badge>
                                                                )}
                                                                {quote.autoResponded && !quote.requiresManualReview && (
                                                                    <Badge color="success" size="sm">
                                                                        {t('badges.autoResponded')}
                                                                    </Badge>
                                                                )}
                                                                {quote.requiresManualReview && !quote.autoResponded && (
                                                                    <Badge color="warning" size="sm">
                                                                        {t('badges.requiresReview')}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                    {quote.isProcessing && quote.processingSteps && quote.processingProgress !== undefined && (
                                                        <ProcessingSteps
                                                            steps={quote.processingSteps}
                                                            progress={quote.processingProgress}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

